import { useMemo, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import IndexedDBProvider from "./libs/use-indexeddb";
import MainLayout from "./pages/MainLayout";
import { useAppStore } from "./store/appStore";

const queryClient = new QueryClient();

const idbConfig = {
	databaseName: "tne2022-db",
	version: 6,
	stores: [
		{
			name: "top10-grouped",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top10",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top12-grouped",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top12",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top14-grouped",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top14",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top16-grouped",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top16",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top18-grouped",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top18",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top21-grouped",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
		{
			name: "top30-grouped",
			id: { keyPath: "id" },
			indices: [{ name: "content", options: { unique: false } }],
		},
	],
};

const App = () => {
	const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
	const darkModeEnabled = useAppStore((state) => state.darkModeEnabled);
	const setDarkModeEnabled = useAppStore((state) => state.setDarkModeEnabled);

	useEffect(() => {
		setDarkModeEnabled(prefersDarkMode);
	}, [setDarkModeEnabled, prefersDarkMode]);

	useEffect(() => {
		console.log("clear localStorage");
		localStorage.clear();
	}, []);

	const theme = useMemo(
		() =>
			createTheme({
				palette: {
					mode: darkModeEnabled ? "dark" : "light",
					background: {
						default: grey[100],
						paper: grey[100],
					},
					...(darkModeEnabled && {
						background: {
							default: grey[900],
							paper: grey[900],
						},
					}),
				},
			}),
		[darkModeEnabled]
	);

	return (
		<IndexedDBProvider config={idbConfig}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<QueryClientProvider client={queryClient}>
					<MainLayout />
				</QueryClientProvider>
			</ThemeProvider>
		</IndexedDBProvider>
	);
};

export default App;
